import React, { useState, useEffect, useRef } from 'react';
import {
  sendPasswordlessCode,
  validatePasswordlessCode
} from '@rechargeapps/storefront-client';
import notification from '../utils/notification';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/wildgrain_logo.svg';
import backgroundWave from '../assets/background-wave.svg';
import { customerAccessTokenCreate, getCustomer as getShopifyCustomer } from '../services/shopify';
import { trackNewPortalLogin, trackPortalVisit } from '../services/api';
import { loginWithShopifyStorefront } from '../services/recharge';
import { ensureShopifyCustomerToken } from '../utils/sessionRefresh';
import { reportError } from '../utils/errorReporting';
import { identifyMemberInClarity } from '../services/clarityTags';
import { initRechargeWithMode, withProxyFallback } from '../utils/rechargeProxyMode';
import Button from './Button';

function Login() {
  const [email, setEmail] = useState('');
  const [passwordEmail, setPasswordEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [sessionToken, setSessionToken] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isGiftRedemption, setIsGiftRedemption] = useState(false);
  const [showOneTimeCode, setShowOneTimeCode] = useState(false);
  const codeEmailRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [passwordMessage] = useState('');

  // Set by MagicLinkRedirect (via router state) when a magic link couldn't be
  // processed: 'expired' | 'invalid'. Lets us explain the silent bounce and
  // steer the member into the one-time-code flow instead.
  const magicLinkError = location.state?.magicLinkError || null;

  const VERIFICATION_CODE_LENGTH = 4;

  // Helper function to get the redirect target and clean up
  const getRedirectTarget = () => {
    const target = localStorage.getItem('loginRedirectTarget');
    localStorage.removeItem('loginRedirectTarget'); // Clean up after reading
    return target || '/overview'; // Default to overview if no target stored
  };

  // Detect whether the user landed here from an (expired) gift-card redemption
  // link. We read the saved target without clearing it so the post-login
  // redirect in getRedirectTarget() still works.
  useEffect(() => {
    try {
      const giftRedemption = localStorage.getItem('loginRedirectTarget') === '/gift/redeem';
      setIsGiftRedemption(giftRedemption);
      // The gift note points users to the one-time-code option, so it must be visible.
      if (giftRedemption) setShowOneTimeCode(true);
    } catch (err) {
      // Ignore storage access issues; just don't show the gift note.
    }
  }, []);

  // After a failed magic link, open the one-time-code section (the fastest
  // recovery path) and prefill the email decoded from the link.
  useEffect(() => {
    if (!magicLinkError) return;
    const linkEmail = location.state?.magicLinkEmail;
    if (linkEmail) setEmail((prev) => prev || linkEmail);
    setShowOneTimeCode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magicLinkError]);

  // Initialize Recharge client (routes via our proxy if this device is flagged)
  useEffect(() => {
    initRechargeWithMode();

    // Load Work Sans font
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@600&display=swap';
    document.head.appendChild(link);
  }, []);

  const handleShowOneTimeCode = () => {
    // Carry over the email the user already typed in the password form
    if (!email && passwordEmail) setEmail(passwordEmail);
    setShowOneTimeCode(true);
  };

  // Focus the code-login email field when the section is revealed by the user
  useEffect(() => {
    if (showOneTimeCode && codeEmailRef.current) {
      codeEmailRef.current.focus();
    }
  }, [showOneTimeCode]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    
    const loadingToast = notification.loading('Sending verification code...');
    
    try {
      // Send passwordless code to user's email
      const response = await withProxyFallback(() => sendPasswordlessCode(email));

      // Store the session token for the verification step
      setSessionToken(response);

      setShowVerification(true);
      notification.dismiss(loadingToast);
      notification.success('Verification code sent to your email!');
    } catch (error) {
      notification.dismiss(loadingToast);
      notification.error(`We weren't able to send a verification code to your email. ${error?.message || 'Please try again.'}`);
      reportError(error, { source: 'Login.handleSendCode' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setMessage('');
    
    const loadingToast = notification.loading('Resending verification code...');
    
    try {
      // Resend passwordless code to user's email
      const response = await withProxyFallback(() => sendPasswordlessCode(email));

      // Update the session token
      setSessionToken(response);
      
      notification.dismiss(loadingToast);
      notification.success('Verification code resent to your email!');
    } catch (error) {
      notification.dismiss(loadingToast);
      notification.error(`We weren't able to resend a verification code to your email. ${error?.message || 'Please try again.'}`);
      reportError(error, { source: 'Login.handleResendCode' });
    } finally {
      setIsResending(false);
    }
  };

  const handleVerificationCodeChange = async (e) => {
    const code = e.target.value;
    
    // Only allow numbers
    if (!/^\d*$/.test(code)) {
      return;
    }
    
    setVerificationCode(code);

    // If code matches expected length and was pasted (not typed)
    if (code.length === VERIFICATION_CODE_LENGTH) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        const form = e.target.form;
        if (form) {
          const submitEvent = new Event('submit', { cancelable: true, bubbles: true });
          form.dispatchEvent(submitEvent);
        }
      }, 100);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // const loadingToast = notification.loading('Verifying code...');
    
    try {
      // Verify the code and log the user in
      const response = await validatePasswordlessCode(email, sessionToken, verificationCode);

      // Store the token in localStorage or state management
      localStorage.setItem('rechargeSession', JSON.stringify(response));

      // Code login authenticates against Recharge only, so mint a Shopify token
      // too — it's what lets us silently refresh this session later. Time-capped
      // so a slow endpoint can't stall the login; a late resolve still lands.
      await Promise.race([
        ensureShopifyCustomerToken({ email }),
        new Promise((resolve) => setTimeout(resolve, 4000)),
      ]);

      // notification.dismiss(loadingToast);
      let firstName = '';
      try {
        // Attempt to retrieve existing Shopify customer access token from localStorage
        const storedTokenRaw = localStorage.getItem('shopifyCustomerAccessToken');
        const storedToken = storedTokenRaw ? JSON.parse(storedTokenRaw) : null;

        if (storedToken?.accessToken) {
          const shopifyCustomer = await getShopifyCustomer(storedToken.accessToken);

          if (shopifyCustomer) {
            const hasNewPortalTag = Array.isArray(shopifyCustomer.tags) && shopifyCustomer.tags.some((t) => t.startsWith('NewPortal'));

            // Extract numeric ID from Shopify GID (e.g., gid://shopify/Customer/12345 -> 12345)
            const customerId = (shopifyCustomer.id || '').split('/').pop();
        identifyMemberInClarity(customerId);

            // Only tag the account that just logged in — never a token another
            // session left on this device.
            const isThisMember = (shopifyCustomer.email || '').trim().toLowerCase() === email.trim().toLowerCase();

            if (!hasNewPortalTag && customerId && isThisMember) {
              (async () => {
                try {
                  await trackNewPortalLogin(customerId);
                } catch (err) {
                  console.warn('NewPortal tag tracking failed:', err);
                }
              })();
            }

            firstName = shopifyCustomer.firstName || '';
          }
        }
      } catch (err) {
        console.error('Failed to retrieve customer info:', err);
      }

      // Fire Klaviyo visit event (non-blocking)
      (async () => {
        try {
          await trackPortalVisit(email, 'passwordless');
        } catch (err) {
          console.error('Failed to send Klaviyo portal visit event:', err);
        }
      })();

      notification.success(`Welcome back${firstName ? ", " + firstName : ''}!`);
      
      // Redirect to target page or overview
      navigate(getRedirectTarget());
    } catch (error) {
      // notification.dismiss(loadingToast);
      notification.error(`We weren't able to verify your code. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setIsPasswordLoading(true);

    // 1. Attempt to authenticate the customer with Shopify
    let customerAccessToken;
    try {
      const createTokenPromise = customerAccessTokenCreate(passwordEmail.trim(), password);

      customerAccessToken = await createTokenPromise;
    } catch (error) {
      console.error('Shopify authentication error:', error);
      // "Unidentified customer" is Shopify's jargon for a credential mismatch —
      // translate it for members and point them at the passwordless option.
      // Other errors (transport failures etc.) keep the raw message, which is
      // what makes them diagnosable.
      if (/unidentified customer/i.test(error?.message || '')) {
        notification.error("That email and password don't match our records. Please double-check them — or use the one-time login code option below.");
        handleShowOneTimeCode();
      } else {
        notification.error(`Login failed. ${error?.message || 'Please check your email & password.'}`);
      }
      reportError(error, { source: 'Login.handlePasswordLogin.shopifyAuth' });
      setIsPasswordLoading(false);
      return;
    }

    // Persist the Shopify access token for any future storefront requests
    try {
      localStorage.setItem('shopifyCustomerAccessToken', JSON.stringify(customerAccessToken));
    } catch (err) {
      console.warn('Unable to save Shopify token to localStorage', err);
    }

    // 2. Use the Shopify token to establish a Recharge session
    let loadingToast;
    try {
      loadingToast = notification.loading('Preparing your account...');

      const session = await withProxyFallback(() => loginWithShopifyStorefront(
        process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
        customerAccessToken.accessToken
      ));

      notification.dismiss(loadingToast);

      // Recharge answers an unusable Shopify token with a token that has no
      // customer behind it (customer_id null + a message). Storing it would
      // log the member into a session that can never load, so fail here.
      if (!session?.apiToken || !session?.customerId) {
        throw new Error(session?.message || 'We could not find a subscription account for this login.');
      }

      // Store the Recharge session for subsequent requests
      try {
        localStorage.setItem('rechargeSession', JSON.stringify(session));
      } catch (err) {
        console.warn('Unable to save Recharge session to localStorage', err);
      }

      // Retrieve customer first name for personalized greeting
      let firstName = '';
      try {
        const shopifyCustomer = await getShopifyCustomer(customerAccessToken.accessToken);

        const hasNewPortalTag = Array.isArray(shopifyCustomer.tags) && shopifyCustomer.tags.some((t) => t.startsWith('NewPortal'));

        const customerId = (shopifyCustomer.id || '').split('/').pop();
        identifyMemberInClarity(customerId);

        if (!hasNewPortalTag && customerId) {
          (async () => {
            try {
              await trackNewPortalLogin(customerId);
            } catch (err) {
              console.warn('NewPortal tag tracking failed:', err);
            }
          })();
        }

        firstName = shopifyCustomer.firstName || '';
      } catch (err) {
        console.error('Failed to retrieve customer info:', err);
      }

      // Fire Klaviyo visit event (non-blocking)
      (async () => {
        try {
          await trackPortalVisit(passwordEmail, 'password');
        } catch (err) {
          console.error('Failed to send Klaviyo portal visit event:', err);
        }
      })();

      notification.success(`Welcome back${firstName ? ", " + firstName : ''}!`);

      // Navigate to target page or overview after successful login
      navigate(getRedirectTarget());
    } catch (error) {
      if (loadingToast) notification.dismiss(loadingToast);
      console.error('Recharge authentication error:', error);
      notification.error(`We couldn't log you in. ${error?.message || 'Please check your email / password and try again.'}`);
      reportError(error, { source: 'Login.handlePasswordLogin.rechargeSession' });
      // Clean up stale Shopify token if Recharge login fails
      localStorage.removeItem('shopifyCustomerAccessToken');
    } finally {
      setIsPasswordLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-4 xxs:py-5 xs:py-6 sm:py-8 relative">
      <div 
        className="absolute inset-0 w-full h-full"
        style={{
          backgroundImage: `url(${backgroundWave})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      <div className="bg-white rounded-2xl ring-1 ring-black/5 p-4 xxs:p-5 xs:p-6 sm:p-8 mx-2 xxs:mx-2.5 sm:mx-0 w-[calc(100%-16px)] xxs:w-[calc(100%-20px)] sm:w-full max-w-md [box-shadow:rgb(113_45_33_/_40%)_0_-20px_100px_-20px] transform transition-all duration-500 ease-in-out motion-reduce:transition-none relative z-10">
        <div className="flex justify-center mb-2 xxs:mb-2.5 xs:mb-3">
          <div className="h-12 w-40 xxs:h-14 xxs:w-44 xs:h-16 xs:w-52 bg-white relative overflow-hidden">
            <img 
              src={logo} 
              alt="Wildgrain Logo" 
              className="absolute inset-0 h-full w-full object-contain"
            />
          </div>
        </div>

        {/* Magic-link failure note (replaces the gift note when both apply,
            so we never stack two banners) */}
        {magicLinkError && (
          <div className="mb-4 xxs:mb-5 p-3 xxs:p-3.5 bg-amber-50 border border-amber-200 rounded-md animate-fade-in">
            <p className="text-xs xxs:text-[13px] xs:text-sm text-gray-700 text-center leading-relaxed">
              <span className="font-semibold">
                {magicLinkError === 'expired'
                  ? 'That login link has expired.'
                  : "We couldn't verify that login link."}
              </span>{' '}
              No problem — enter your email below and we'll send you a one-time code to log
              {isGiftRedemption ? ' in and access your gift.' : ' right in.'}
            </p>
          </div>
        )}

        {/* Gift-card redemption helper note */}
        {isGiftRedemption && !magicLinkError && (
          <div className="mb-4 xxs:mb-5 p-3 xxs:p-3.5 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-xs xxs:text-[13px] xs:text-sm text-gray-700 text-center leading-relaxed">
              <span className="font-semibold">Redeeming a gift card?</span> Use the{' '}
              <span className="font-semibold">“login with a one time code”</span> option below — enter
              your email and we'll send you a code to access your gift.
            </p>
          </div>
        )}
        
        {/* Password login section */}
        <div className="mb-5 xxs:mb-6 xs:mb-8">
          <div className="text-center mb-3 xxs:mb-3.5 xs:mb-4">
            <p className="text-md font-semibold text-gray-600 relative">
              <span className="relative z-10 px-2 xxs:px-3 xs:px-4 bg-white text-xs xxs:text-[13px] xs:text-sm">Login with your password</span>
              <span aria-hidden="true" className="absolute inset-x-0 top-1/2 h-px bg-gray-200 -z-0"></span>
            </p>
          </div>

          <form onSubmit={handlePasswordLogin}>
            <div className="mb-3 xxs:mb-3.5 xs:mb-4">
              <label htmlFor="passwordEmail" className="block mb-1.5 xxs:mb-1.5 xs:mb-2 font-semibold text-gray-700 text-xs xxs:text-[13px] xs:text-sm">Email</label>
              <input
                type="email"
                id="passwordEmail"
                value={passwordEmail}
                onChange={(e) => setPasswordEmail(e.target.value)}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-xs xxs:text-[13px] xs:text-sm text-gray-700"
                placeholder="your@email.com"
              />
            </div>

            <div className="mb-3 xxs:mb-3.5 xs:mb-4">
              <label htmlFor="password" className="block mb-1.5 xxs:mb-1.5 xs:mb-2 font-semibold text-gray-700 text-xs xxs:text-[13px] xs:text-sm">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-xs xxs:text-[13px] xs:text-sm text-gray-700"
                placeholder="Your password"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="sm"
              fullWidth
              loading={isPasswordLoading}
              disabled={isPasswordLoading}
            >
              Login
            </Button>

            <div className="mt-2 text-xs xxs:text-[13px] xs:text-sm text-gray-600 text-center">
              <a href="/forgot-password" className="text-primary hover:underline">Forgot your password?</a>
            </div>

            {passwordMessage && (
              <div className="mt-3 xxs:mt-3.5 xs:mt-4 p-2 xxs:p-2.5 xs:p-3 bg-blue-50 text-primary rounded-md text-center text-xs xxs:text-[13px] xs:text-sm">
                {passwordMessage}
              </div>
            )}
          </form>
        </div>

        {/* Divider with toggle to reveal the one-time-code login */}
        <div className="text-center mb-3 xxs:mb-3.5 xs:mb-4">
          <p className="text-md font-semibold relative">
            <span aria-hidden="true" className="absolute inset-x-0 top-1/2 h-px bg-gray-200 -z-0"></span>
            {showOneTimeCode ? (
              <span className="relative z-10 px-2 xxs:px-3 xs:px-4 bg-white text-xs xxs:text-[13px] xs:text-sm text-gray-600">or login with a one time code</span>
            ) : (
              <button
                type="button"
                onClick={handleShowOneTimeCode}
                className="relative z-10 px-2 xxs:px-3 xs:px-4 bg-white text-xs xxs:text-[13px] xs:text-sm font-semibold text-primary hover:underline"
              >
                or email me a one time login code
              </button>
            )}
          </p>
        </div>

        {/* One-time code login section */}
        {showOneTimeCode && (
        <div className="mb-5 xxs:mb-6 xs:mb-8">
          
          {!showVerification ? (
            <form onSubmit={handleSendCode}>
              <div className="mb-3 xxs:mb-3.5 xs:mb-4">
                <label htmlFor="email" className="block mb-1.5 xxs:mb-1.5 xs:mb-2 font-semibold text-gray-700 text-xs xxs:text-[13px] xs:text-sm">Email</label>
                <input
                  ref={codeEmailRef}
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="off"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-xs xxs:text-[13px] xs:text-sm text-gray-700"
                  placeholder="Enter your email"
                />
              </div>
              
              <Button
                type="submit"
                variant="primary"
                size="sm"
                fullWidth
                loading={isLoading}
                disabled={isLoading}
              >
                Send me a login code
              </Button>
              
              {message && (
                <div className="mt-3 xxs:mt-3.5 xs:mt-4 p-2 xxs:p-2.5 xs:p-3 bg-blue-50 text-primary rounded-md text-center text-xs xxs:text-[13px] xs:text-sm">
                  {message}
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={handleVerifyCode}>
              <div className="mb-3 xxs:mb-3.5 xs:mb-4">
                <label htmlFor="code" className="block mb-1.5 xxs:mb-1.5 xs:mb-2 font-semibold text-gray-700 text-xs xxs:text-[13px] xs:text-sm">Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  id="code"
                  value={verificationCode}
                  onChange={handleVerificationCodeChange}
                  required
                  maxLength={VERIFICATION_CODE_LENGTH}
                  autoComplete="one-time-code"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-xs xxs:text-[13px] xs:text-sm text-gray-700"
                  placeholder="Enter 4-digit code"
                />
              </div>
              
              <Button
                type="submit"
                variant="primary"
                size="sm"
                fullWidth
                loading={isLoading}
                disabled={isLoading}
              >
                Verify Code
              </Button>
              
              <Button
                type="button"
                onClick={handleResendCode}
                variant="text"
                size="sm"
                fullWidth
                className="mt-3"
                loading={isResending}
                disabled={isResending}
              >
                Resend verification code
              </Button>
              
              {message && (
                <div className="mt-3 xxs:mt-3.5 xs:mt-4 p-2 xxs:p-2.5 xs:p-3 bg-blue-50 text-primary rounded-md text-center text-xs xxs:text-[13px] xs:text-sm">
                  {message}
                </div>
              )}
            </form>
          )}
        </div>
        )}
      </div>
      
      {/* Toast container */}
      {/* <Toaster position="bottom-center" toastOptions={{ duration: 3000 }} /> */}
    </div>
  );
}

export default Login;